import React, { Suspense } from 'react';
import { GlobalMessage } from 'components/GlobalMessage/GlobalMessage';
import LoadingWrapper from 'components/LoadingWrapper';
// import { defaultTheme } from '@timeedit/ui-components/theme/token';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useAopTopBar } from 'microFrontend/hooks/useAopTopBar';
import { configService } from 'services/config.service';
import {
  fetchInitialData,
  fetchTEUserFromToken,
  onLoginSuccess,
  onLogout,
} from 'slices/auth.slice';
import { organizationSelector } from 'slices/organization.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { parseCustomerSignature } from 'utils/customerSignature';
import { AuthProvider } from '@timeedit/auth-client/react';
import type { ITEToken } from '@timeedit/types';
import { EAppId } from '@timeedit/types/lib/enums';
import { createOrganizationContextFromToken } from '@timeedit/types/lib/types/launchDarkly.type';
import { notification } from '@timeedit/ui-components/antd';
import './App.scss';

// Lazy to prevent module federation code from being loaded when not used
const AopRouter = React.lazy(() => import('microFrontend/AopRouter'));
const Router = React.lazy(() => import('routes/Router'));

dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale(dayjs.locale(), {
  weekStart: 1,
  yearStart: 4, // ISO 8601: week 1 is the first week containing January 4
});

notification.config({
  placement: 'topRight',
  duration: 3,
  top: 58,
});

const App = () => {
  const dispatch = useAppDispatch();
  const ldClient = useLDClient();

  const organization = useAppSelector(organizationSelector);

  const useAopTopbar = useAopTopBar();

  /**
   * EVENT HANDLERS
   */
  const setLoginToRedux = (userToken: ITEToken) => {
    try {
      ldClient
        .identify(createOrganizationContextFromToken(userToken))
        .catch((err) => {
          console.error('Error identifying LaunchDarkly organization', err);
        });
    } catch (err) {
      console.error('Error creating context for LaunchDarkly', err);
    }

    dispatch(onLoginSuccess(userToken));
    dispatch(fetchInitialData());
    dispatch(fetchTEUserFromToken(userToken));
  };

  const logoutFromRedux = () => {
    ldClient.close().catch((err) => {
      console.error('Error closing LaunchDarkly client', err);
    });

    dispatch(onLogout());
  };

  // TODO: Update to use a feature toggle for this
  const enableLogs =
    import.meta.env.DEV || import.meta.env.VITE_TE_APP_ENV === 'beta';

  return (
    <>
      <GlobalMessage />
      <AuthProvider
        appId={EAppId.TE_ADMIN}
        authUrl={configService.REACT_APP_AUTH_DOMAIN}
        onLoginSuccess={setLoginToRedux}
        onLogout={logoutFromRedux}
        enableLogs={enableLogs}
        customerSignature={parseCustomerSignature(
          organization?.customerSignature
        )}
      >
        <Suspense fallback={<LoadingWrapper isLoading />}>
          {useAopTopbar ? <AopRouter /> : <Router />}
        </Suspense>
      </AuthProvider>
    </>
  );
};

export default App;
